import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import type { PlacementBucket } from '../../types/placement';
import {
    PLACEMENT_BUCKET_COLORS,
} from '../../types/placement';

interface PlacementBadgeProps {
    bucket: PlacementBucket;
    /** If true, render as a small dot (icon-only). Default: show short label. */
    dotOnly?: boolean;
    /** Skip the built-in tooltip when a parent already explains the type. */
    hideTooltip?: boolean;
}

const PlacementBadge: React.FC<PlacementBadgeProps> = ({
    bucket,
    dotOnly = false,
    hideTooltip = false,
}) => {
    const { t } = useTranslation('explorer');
    const color = PLACEMENT_BUCKET_COLORS[bucket];
    const badge = (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'default',
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                    display: 'inline-block',
                }}
            />
            {!dotOnly && (
                <span
                    style={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color,
                        borderBottom: `1px dotted ${color}`,
                        lineHeight: 1.2,
                    }}
                >
                    {t(`placement.short.${bucket}`)}
                </span>
            )}
        </span>
    );

    if (hideTooltip) return badge;

    return (
        <Tooltip title={t(`placement.description.${bucket}`)} arrow placement="top">
            {badge}
        </Tooltip>
    );
};

/**
 * Inline strip showing non-zero bucket counts separated by dots.
 * Mirrors the pattern in WeeklyMovementsSection lines 62–88.
 */
interface PlacementStripProps {
    contentMentions?: number | null;
    physicalMentions?: number | null;
    creditsMentions?: number | null;
    bannerMentions?: number | null;
}

export const PlacementStrip: React.FC<PlacementStripProps> = ({
    contentMentions,
    physicalMentions,
    creditsMentions,
    bannerMentions,
}) => {
    const { t } = useTranslation('explorer');
    const parts: Array<{ bucket: PlacementBucket; count: number }> = (
        [
            { bucket: 'content' as PlacementBucket, count: contentMentions ?? 0 },
            { bucket: 'physical' as PlacementBucket, count: physicalMentions ?? 0 },
            { bucket: 'banner' as PlacementBucket, count: bannerMentions ?? 0 },
            { bucket: 'credits' as PlacementBucket, count: creditsMentions ?? 0 },
        ] as Array<{ bucket: PlacementBucket; count: number }>
    ).filter((p) => p.count > 0);

    if (parts.length === 0) return null;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {parts.map((p, i) => (
                <React.Fragment key={p.bucket}>
                    {i > 0 && (
                        <span style={{ opacity: 0.4, fontSize: '0.7rem' }}>·</span>
                    )}
                    <Tooltip title={t(`placement.description.${p.bucket}`)} arrow placement="top">
                        <span
                            style={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: PLACEMENT_BUCKET_COLORS[p.bucket],
                                borderBottom: `1px dotted ${PLACEMENT_BUCKET_COLORS[p.bucket]}`,
                                cursor: 'default',
                                lineHeight: 1.2,
                            }}
                        >
                            {p.count} {t(`placement.short.${p.bucket}`)}
                        </span>
                    </Tooltip>
                </React.Fragment>
            ))}
        </span>
    );
};

export default PlacementBadge;
